import React, { useState, useContext } from "react";
import styled from "styled-components";
import { ListGroup, Collapse } from "react-bootstrap";
import { FaAngleRight, FaAngleDown } from "react-icons/fa";
import { Link, navigate } from "gatsby";
import GlobalContext from "../../context/GlobalContext";
import { AuthContext } from "../../context/AuthContext";
import { useWalletSelector } from "../../context/WalletSelectorContext";

import notify from "../../utils/notify";
import { magic } from "../../utils/magic";
import NearIcon from "../../assets/image/svg/near_icon.svg";
import NearIconWhite from "../../assets/image/svg/near_icon_wht.svg";



const NestedMenuContainer = styled.div`
  a,
  .label {
    color: ${({ dark, theme }) =>
    dark ? theme.colors.light : theme.colors.heading}!important;
    font-size: 15px;
    font-weight: 500;
    transition: all 0.3s ease-out;
    &:hover,
    &:active {
      color: ${({ theme }) => theme.colors.primary};
      text-decoration: none;
    }
  }

  .list-group-item {
    & + .collapse:not(.show) {
      .list-group-item {
        border: none !important;
        border-width: 0 !important;
      }
    }
    & + .collapse.show {
      .list-group-item {
        &:first-child {
          border-top: none !important;
        }
      }
    }
  }
  .collapse + .list-group-item {
    border-top-width: 0;
  }
  /* .list-group-flush:last-child .list-group-item:last-child {
    border-bottom-width: 1px;
  } */
`;

const defaultMenuItems = [
  { name: "home", label: "Home" },
  {
    name: "billing",
    label: "Billing",
    items: [
      { name: "statements", label: "Statements" },
      { name: "reports", label: "Reports" },
    ],
  },
  {
    name: "settings",
    label: "Settings",
    items: [
      { name: "profile", label: "Profile" },
      { name: "insurance", label: "Insurance" },
      {
        name: "notifications",
        label: "Notifications",
        items: [
          { name: "email", label: "Email" },
          {
            name: "desktop",
            label: "Desktop",
            items: [
              { name: "schedule", label: "Schedule" },
              { name: "frequency", label: "Frequency" },
            ],
          },
          { name: "sms", label: "SMS" },
        ],
      },
    ],
  },
];

const MenuItem = ({
  label,
  isExternal = false,
  name,
  items,
  depthStep = 20,
  depth = 0,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const hasSubItems = Array.isArray(items);

  const gContext = useContext(GlobalContext);

  return (
    <>
      {hasSubItems ? (
        <ListGroup.Item
          {...rest}
          css={`
            padding-left: ${depth * depthStep}px !important;
            cursor: pointer;
          `}
          onClick={() => setOpen(!open)}
          className="d-flex align-items-center justify-content-between"
        >
          <span className="label">{label}</span>
          <span>{open ? <FaAngleDown /> : <FaAngleRight />}</span>
        </ListGroup.Item>
      ) : (
        <ListGroup.Item
          {...rest}
          css={`
            padding-left: ${depth * depthStep}px !important;
          `}
        >
          {isExternal ? (
            <a
              href={`${name}`}
              onClick={() => {
                if (gContext.visibleOffCanvas) {
                  gContext.toggleOffCanvas();
                }
              }}
            >
              {label}
            </a>
          ) : (
            <Link
              to={`/${name}`}
              onClick={() => {
                if (gContext.visibleOffCanvas) {
                  gContext.toggleOffCanvas();
                }
              }}
            >
              {label}
            </Link>
          )}
        </ListGroup.Item>
      )}

      {hasSubItems ? (
        <Collapse in={open}>
          <ListGroup>
            {items.map((subItem) => (
              <MenuItem
                key={subItem.name}
                depth={depth + 1}
                depthStep={depthStep}
                {...subItem}
              />
            ))}
          </ListGroup>
        </Collapse>
      ) : null}
    </>
  );
};

const NestedMenu = ({ menuItems = defaultMenuItems }) => {
  const gContext = useContext(GlobalContext);
  const { auth, logoutUser } = useContext(AuthContext);
  const { selector, modal, accounts, accountId } = useWalletSelector();

  const [showLoginOptions, SetShowLoginOptions] = useState(false)

  const handleLogout = async (e) => {
    try {
      e.preventDefault();
      notify("Logging you out...", "success");
      const isMagicUserLoggedIn = await magic.user.isLoggedIn();
      if (selector.isSignedIn()) {
        const wallet = await selector.wallet();
        await wallet.signOut();
        await logoutUser();
        navigate("/", { replace: true });
      } else if (isMagicUserLoggedIn) {
        await magic.user.logout();
        await logoutUser();
        navigate("/", { replace: true });
      } else {
        await modal.show();
        await handleLogout();
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <NestedMenuContainer>
      <ListGroup variant="flush">
        {menuItems.map((menuItem, index) => (
          <MenuItem
            key={`${menuItem.name}${index}`}
            depthStep={20}
            depth={0}
            {...menuItem}
          />
        ))}
        {/* Auth buttons */}
        <ListGroup.Item
          css={`
            padding-left: 0px !important;
          `}
          className="py-8"
        >
          {auth ? (
            <div>
              <button
                className="btn btn-outline-dark text-uppercase font-size-3 font-weight-semibold focus-reset mb-6"
                type="link"
                onClick={(e) => {
                  e.preventDefault();
                  if (gContext.visibleOffCanvas) {
                    gContext.toggleOffCanvas();
                  }
                  navigate("/dashboard-main");
                }}
              >
                Dashboard
              </button>
              {!auth?.talent ? (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (gContext.visibleOffCanvas) {
                      gContext.toggleOffCanvas();
                    }
                    navigate("/post-job");
                  }}
                  className={`btn btn-${gContext.header.variant} text-uppercase font-weight-semibold font-size-3 focus-reset mb-6`}
                >
                  Post Job
                </button>
              ) : null}
              <button
                className="btn btn-outline-dark font-size-3 font-weight-semibold text-uppercase"
                onClick={(e) => {
                  if (gContext.visibleOffCanvas) {
                    gContext.toggleOffCanvas();
                  }
                  handleLogout(e);
                }}
              >
                Log Out
              </button>
            </div>
          ) : (
            <div>
              <button
                //className={`btn btn-${gContext.header.variant} text-uppercase font-weight-semibold font-size-3 focus-reset`}
                className="btn btn-outline-dark text-uppercase font-size-3 heading-default-color focus-reset mx-4 px-4 d-flex near-login-btn mb-2"
                onClick={() => SetShowLoginOptions(!showLoginOptions)}
              >
                <span className="">Login with</span>
                <img
                  src={NearIcon}
                  alt="near icon"
                  height={34}
                  className="near-login-icon"
                />
                <img
                  src={NearIconWhite}
                  alt="near icon"
                  height={34}
                  className="near-login-icon-white"
                />
              </button>


              {showLoginOptions ? (
                <div>
                  <div
                    className=" btn border-bottom text-uppercase font-weight-semibold font-size-3 heading-default-color focus-reset px-4 near-login-btn mb-6"

                    onClick={(e) => {
                      e.preventDefault();
                      if (gContext.visibleOffCanvas) {
                        gContext.toggleOffCanvas();
                      }

                      modal.show()

                    }}
                  >
                    Existing NEAR user

                  </div>

                  <div
                    className=" btn border-bottom text-uppercase font-weight-semibold font-size-3 heading-default-color focus-reset px-4 near-login-btn mb-6"

                    onClick={(e) => {
                      e.preventDefault();
                      if (gContext.visibleOffCanvas) {
                        gContext.toggleOffCanvas();
                      }
                      navigate("/auth");
                    }}
                  >
                    New with NEAR?
                  </div>
                </div>
              ) : null}

              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (gContext.visibleOffCanvas) {
                    gContext.toggleOffCanvas();
                  }
                  navigate("/post-job");
                }}
                className={`btn btn-${gContext.header.variant} text-uppercase font-weight-semibold font-size-3 focus-reset px-4 mx-4`}
              >
                Post Job
              </button>
            </div>



          )}
        </ListGroup.Item>
      </ListGroup>
    </NestedMenuContainer>
  );
};

export default NestedMenu;
